import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigation, useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SearchBox from '@/components/General/inputs/SearchBox';
import SelectInput from '@/components/General/inputs/SelectInput';
import SolidButton from '@/components/General/SolidButton';
import TaskCard from '@/components/Tasks/TaskCard';
import Unicon from '@/components/General/Unicon';
import { deleteTask, deleteTaskInstance } from '@/data/tasks';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { showAlert } from '@/utils/showAlert';
import { isAutomationDueType, isFollowUpDueType } from '@/utils/constants/taskTypes';

const taskTypes = [
    { key: 'EVENT_VOLUNTEER', label: 'Volunteer' },
    { key: 'EVENT_SERVICE_FLOW', label: 'Service Flow' },
    { key: 'INDIVIDUAL', label: 'Individual' },
    { key: 'PATHS', label: 'Automations' },
    // Care-task definitions stay scoped to the Follow Up team — helpers and
    // other org members never browse care work in the library (spec §8.4).
    { key: 'FOLLOW_UP', label: 'Follow Up', requiresFollowUpView: true },
];

const TaskDefinitionCard = ({ task, loggedInUser, onDelete }) => {
    const router = useRouter();
    const teams = task?.VolunteerTeamTasks?.map(item => item.VolunteerTeam).filter(Boolean) || [];
    const options = [];
    // Care-task definitions are created and managed through the Follow Up
    // surface — generic edit/delete can evade the care context (spec §8.4).
    const isCareDefinition = isFollowUpDueType(task?.dueType);

    if (!isCareDefinition && loggedInUser?.can?.('Update Tasks')) {
        options.push({
            name: 'Edit Task',
            icon: dropdownIcons.edit,
            onSelect: () => router.push({ pathname: '/tasks/create', params: { id: task.id } }),
        });
    }
    if (!isCareDefinition && loggedInUser?.can?.('Delete Tasks')) {
        options.push({
            name: 'Delete Task',
            icon: dropdownIcons.delete,
            destructive: true,
            onSelect: () => showAlert({
                title: 'Delete Task',
                subtitle: `Are you sure you want to delete "${task.title}"? This will also delete all occurrences. This cannot be undone.`,
                isDestructive: true,
                confirmText: 'Delete',
                onConfirm: () => onDelete(task.id),
            }),
        });
    }

    return (
        <View testID={`TaskCard-${task.title}`} style={styles.card}>
            <TouchableOpacity testID={`TaskCardLink-${task.id}`} accessibilityRole="button" accessibilityLabel={`Open ${task.title}`} style={styles.cardMain} onPress={() => router.push(`/tasks/${task.id}`)} activeOpacity={0.7}>
                <View style={styles.cardIcon}>
                    <Unicon name="clipboard-outline" size={20} colorVariant="tasks" />
                </View>
                <View style={styles.cardContent}>
                    <Text style={styles.cardTitle}>{task.title}</Text>
                    {!!task.description && <Text numberOfLines={2} style={styles.cardDescription}>{task.description}</Text>}
                    <View style={styles.metaRow}>
                        {teams.length > 0 && <Text style={styles.metaText}>{teams.map(team => team.name).join(', ')}</Text>}
                        {task.Events?.length > 0 && <Text style={styles.metaText}>{task.Events.length} event{task.Events.length === 1 ? '' : 's'}</Text>}
                    </View>
                </View>
            </TouchableOpacity>
            {options.length > 0 && <DropdownMenu testID={`TaskCardMenu-${task.id}`} options={options} />}
        </View>
    );
};

export default function TaskLibraryScreen() {
    const [activeType, setActiveType] = useState(taskTypes[0].key);
    const [searchText, setSearchText] = useState('');
    const [teamID, setTeamID] = useState('all');
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const canViewFollowUp = !!loggedInUser?.can?.('View Follow Up');
    const visibleTaskTypes = useMemo(
        () => taskTypes.filter(t => !t.requiresFollowUpView || canViewFollowUp),
        [canViewFollowUp]
    );
    const { data: tasks = [], isFetching: tasksFetching } = Query.tasks.all();
    const { data: teams = [] } = Query.taskTeams.all();
    const { data: individualInstances = [], isFetching: instancesFetching } = Query.taskInstances.individual();

    useEffect(() => {
        navigation.setOptions({
            headerRight: () => loggedInUser?.can?.('Create Tasks') ? (
                <DropdownMenu
                    testID="TaskLibraryHeaderMenu"
                    options={[
                        { name: 'Create Task', icon: dropdownIcons.add, onSelect: () => router.push('/tasks/create') },
                        { name: 'Schedule Tasks', icon: dropdownIcons.calendar, onSelect: () => router.push('/tasks/schedule') },
                    ]}
                />
            ) : null,
        });
    }, [loggedInUser, navigation, router]);

    const deleteTaskMutation = useMutation({
        mutationFn: taskID => deleteTask({ taskID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.individual() });
        },
    });
    const deleteInstanceMutation = useMutation({
        mutationFn: deleteTaskInstance,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.individual() });
        },
    });

    const filteredTasks = useMemo(() => tasks.filter(task => {
        // 'PATHS' chip covers the whole automations family (dual-read).
        const matchesType = activeType === 'PATHS' ? isAutomationDueType(task.dueType) : task.dueType === activeType;
        const matchesSearch = !searchText || task.title?.toLowerCase().includes(searchText.toLowerCase()) || task.description?.toLowerCase().includes(searchText.toLowerCase());
        const matchesTeam = activeType !== 'EVENT_VOLUNTEER' || teamID === 'all' || task.VolunteerTeamTasks?.some(item => item.VolunteerTeam?.id === teamID);
        return matchesType && matchesSearch && matchesTeam;
    }), [activeType, searchText, tasks, teamID]);

    const filteredInstances = useMemo(() => individualInstances.filter(instance => {
        const matchesSearch = !searchText || instance.Task?.title?.toLowerCase().includes(searchText.toLowerCase()) || instance.Task?.description?.toLowerCase().includes(searchText.toLowerCase());
        return matchesSearch;
    }), [individualInstances, searchText]);

    const refresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.taskTeams.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.individual() });
    }, [queryClient]);

    const loading = activeType === 'INDIVIDUAL' ? instancesFetching : tasksFetching;
    const results = activeType === 'INDIVIDUAL' ? filteredInstances : filteredTasks;

    return (
        <ScrollView
            style={styles.root}
            contentContainerStyle={styles.content}
            keyboardShouldPersistTaps="handled"
            refreshControl={<RefreshControl refreshing={false} onRefresh={refresh} />}
        >
            <View style={styles.pageHeader}>
                <View style={styles.headingCopy}>
                    <Text style={styles.eyebrow}>TASK DEFINITIONS</Text>
                    <Text style={styles.heading}>Task library</Text>
                    <Text style={styles.subheading}>Find, update, and schedule reusable work.</Text>
                </View>
                {loggedInUser?.can?.('Create Tasks') && <SolidButton testID="TaskLibraryCreateTask" text="Create task" iconName="add" iconBefore onPress={() => router.push('/tasks/create')} />}
            </View>

            <SearchBox testID="TaskLibrarySearch" searchText={searchText} onChangeText={setSearchText} textChangeDelay={0} placeholder="Search task library..." />

            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterRow}>
                {visibleTaskTypes.map(type => (
                    <TouchableOpacity
                        key={type.key}
                        testID={`TaskLibraryType-${type.key}`}
                        accessibilityRole="button"
                        accessibilityLabel={`Filter by ${type.label} tasks`}
                        accessibilityState={{ selected: activeType === type.key }}
                        style={styles.filterChip(activeType === type.key)}
                        onPress={() => {
                            setActiveType(type.key);
                            if (type.key !== 'EVENT_VOLUNTEER') setTeamID('all');
                        }}
                    >
                        <Text style={styles.filterText(activeType === type.key)}>{type.label}</Text>
                    </TouchableOpacity>
                ))}
            </ScrollView>

            {activeType === 'EVENT_VOLUNTEER' && teams.length > 0 && (
                <SelectInput
                    testID="TaskLibraryTeamFilter"
                    title="Team"
                    placeholder="All teams"
                    value={teamID === 'all' ? null : { label: teams.find(team => team.id === teamID)?.name, value: teamID }}
                    onUpdate={value => setTeamID(value || 'all')}
                    options={[{ label: 'All teams', value: 'all' }, ...teams.map(team => ({ label: team.name, value: team.id }))]}
                />
            )}

            <View style={styles.list}>
                {loading ? <ActivityIndicator style={styles.loading} /> : results.length === 0 ? (
                    <EmptyListPlaceholder spacing={10} message={searchText ? 'No tasks match your search.' : 'No tasks in this category yet.'} />
                ) : activeType === 'INDIVIDUAL' ? filteredInstances.map(instance => (
                    <TaskCard
                        key={instance.id}
                        taskInstance={instance}
                        showEvent={false}
                        fromScreen="/tasks/library"
                        actions={loggedInUser?.can?.('Delete Tasks') ? [{
                            label: 'Delete',
                            icon: 'trash',
                            color: 'error',
                            fn: () => showAlert({
                                title: 'Delete Task',
                                subtitle: `Are you sure you want to delete "${instance.Task?.title}"? This cannot be undone.`,
                                isDestructive: true,
                                confirmText: 'Delete',
                                onConfirm: () => deleteInstanceMutation.mutate(instance.id),
                            }),
                        }] : []}
                    />
                )) : filteredTasks.map(task => (
                    <TaskDefinitionCard key={task.id} task={task} loggedInUser={loggedInUser} onDelete={taskID => deleteTaskMutation.mutate(taskID)} />
                ))}
            </View>
        </ScrollView>
    );
}

const styles = StyleSheet.create(theme => ({
    root: { flex: 1, backgroundColor: 'transparent' },
    content: { width: '100%', maxWidth: 920, alignSelf: 'center', padding: 16, paddingBottom: 100, gap: 14 },
    pageHeader: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16 },
    headingCopy: { flex: 1 },
    eyebrow: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 11, letterSpacing: 1.2 },
    heading: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 24, marginTop: 3 },
    subheading: { color: theme.colors.textSecondary, fontSize: 14, marginTop: 3 },
    filterRow: { gap: 8, paddingVertical: 2 },
    filterChip: active => ({ backgroundColor: active ? theme.colors.tasksContainer : theme.colors.surface, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 9 }),
    filterText: active => ({ color: active ? theme.colors.tasks : theme.colors.textSecondary, fontFamily: active ? theme.fonts.bold : theme.fonts.body, fontSize: 13 }),
    list: { gap: 10 },
    loading: { marginVertical: 48 },
    card: { flexDirection: 'row', alignItems: 'center', backgroundColor: theme.colors.surface, borderRadius: 14, padding: 14, boxShadow: theme.shadows.card },
    cardMain: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12 },
    cardIcon: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center', borderRadius: 10, backgroundColor: theme.colors.tasksContainer },
    cardContent: { flex: 1 },
    cardTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 15 },
    cardDescription: { color: theme.colors.textSecondary, fontSize: 13, lineHeight: 18, marginTop: 2 },
    metaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 6 },
    metaText: { color: theme.colors.textSecondary, fontSize: 11 },
}));
